import { useState, useEffect } from "react";
import { Key, Plus, Copy, Trash2, CheckCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API_BASE = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api/v1`
  : "https://mnemonic-production.up.railway.app/api/v1";

export default function APIKeysView() {
  const { apiFetch } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchAPIKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAPIKeys = async () => {
    try {
      const response = await apiFetch("/api-keys");
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data);
      }
    } catch (error) {
      if (error.message !== "Session expired. Please log in again.") {
        console.error("Error fetching API keys:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const createAPIKey = async () => {
    if (!newKeyName.trim()) return;

    setCreating(true);
    try {
      const response = await apiFetch("/api-keys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newKeyName }),
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedKey(data);
        setNewKeyName("");
        setShowCreateModal(false);
        fetchAPIKeys();
        toast.success("API key created — copy it now, it won't be shown again.");
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || `Failed to create API key (${response.status})`);
      }
    } catch (error) {
      if (error.message !== "Session expired. Please log in again.") {
        toast.error("Failed to create API key. Check your connection and try again.");
      }
    } finally {
      setCreating(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const response = await apiFetch(`/api-keys/${deleteTarget.id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setDeleteTarget(null);
        fetchAPIKeys();
        toast.success(`Key "${deleteTarget.name}" deleted.`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || `Failed to delete API key (${response.status})`);
      }
    } catch (error) {
      if (error.message !== "Session expired. Please log in again.") {
        toast.error("Failed to delete API key. Check your connection and try again.");
      }
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = (text, label = "Copied") => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(label);
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-cyber mb-2">
            / control.room / api-keys
          </div>
          <h1 className="font-display font-black uppercase text-3xl md:text-4xl tracking-tight">
            API <span className="text-cyber">Keys</span>
          </h1>
          <p className="text-ink-500 text-sm mt-2">
            Manage authentication keys for MnemonicAI
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 bg-cyber text-void px-4 py-2 text-xs font-bold tracking-wider uppercase hover:shadow-[0_0_16px_rgba(0,229,255,0.4)] transition-shadow"
        >
          <Plus size={14} /> New Key
        </button>
      </div>

      {/* One-time key reveal */}
      {createdKey && (
        <div className="bg-terminal/10 border border-terminal p-5">
          <div className="flex items-start gap-3">
            <CheckCircle size={18} className="text-terminal mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-bold text-terminal text-sm mb-1">Key created successfully</div>
              <p className="text-xs text-ink-400 mb-3">{createdKey.warning}</p>
              <div className="bg-void border border-line-2 p-3 font-mono text-sm text-terminal break-all">
                {createdKey.key}
              </div>
              <button
                onClick={() =>
                  copyToClipboard(createdKey.key, "API key copied to clipboard!")
                }
                className="mt-3 inline-flex items-center gap-2 text-xs text-terminal hover:text-terminal/80 transition-colors"
              >
                <Copy size={13} /> Copy to clipboard
              </button>
            </div>
            <button
              onClick={() => setCreatedKey(null)}
              className="text-ink-500 hover:text-ink-100 text-lg leading-none"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Keys list */}
      {loading ? (
        <div className="text-center py-12 text-ink-500 text-[10px] tracking-[0.3em] uppercase animate-pulse">
          Loading…
        </div>
      ) : apiKeys.length === 0 ? (
        <div className="text-center py-12 border border-line-1 bg-surface">
          <Key size={40} className="mx-auto mb-4 text-ink-500" strokeWidth={1.5} />
          <p className="text-ink-300 text-sm mb-1">No API keys yet</p>
          <p className="text-xs text-ink-500 mb-5">
            Create a key to start using the MnemonicAI API
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 bg-cyber text-void px-4 py-2 text-xs font-bold tracking-wider uppercase"
          >
            <Plus size={14} /> Create Your First Key
          </button>
        </div>
      ) : (
        <div className="border border-line-1 bg-surface">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-[0.3em] uppercase text-ink-500 border-b border-line-1">
                <th className="text-left px-5 py-3 font-normal">Name</th>
                <th className="text-left px-5 py-3 font-normal">Prefix</th>
                <th className="text-left px-5 py-3 font-normal">Status</th>
                <th className="text-left px-5 py-3 font-normal">Created</th>
                <th className="text-left px-5 py-3 font-normal">Last used</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((key) => (
                <tr
                  key={key.id}
                  className="border-b border-line-1 hover:bg-cyber/5 transition-colors group"
                >
                  <td className="px-5 py-3 font-semibold text-ink-100">{key.name}</td>
                  <td className="px-5 py-3 font-mono text-xs text-ink-400">
                    <span className="flex items-center gap-2">
                      {key.key_prefix}
                      <button
                        onClick={() => copyToClipboard(key.key_prefix, "Prefix copied")}
                        className="opacity-0 group-hover:opacity-100 text-ink-500 hover:text-cyber transition-all"
                        title="Copy prefix"
                      >
                        <Copy size={12} />
                      </button>
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`text-[10px] px-2 py-0.5 uppercase tracking-widest ${
                        key.status === "active"
                          ? "bg-terminal/20 text-terminal"
                          : "bg-hotpink/20 text-hotpink"
                      }`}
                    >
                      {key.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-ink-500">
                    {new Date(key.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-5 py-3 text-xs text-ink-500">
                    {key.last_used_at
                      ? new Date(key.last_used_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => setDeleteTarget({ id: key.id, name: key.name })}
                      disabled={key.status === "revoked"}
                      className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase border border-hotpink/30 text-hotpink/70 px-2.5 py-1 hover:bg-hotpink/10 hover:border-hotpink hover:text-hotpink transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Delete key"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-void/80 backdrop-blur-sm">
          <div className="bg-surface border border-line-1 max-w-md w-full mx-4 p-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-cyber mb-1">
              new api key
            </div>
            <h2 className="font-display font-bold text-xl mb-5">Create API Key</h2>
            <div className="mb-5">
              <label className="block text-xs text-ink-400 mb-2 tracking-wider uppercase">
                Key name
              </label>
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createAPIKey()}
                placeholder="e.g. Production · Claude Code"
                className="w-full bg-void border border-line-2 px-4 py-2.5 text-ink-100 text-sm focus:border-cyber focus:outline-none placeholder:text-ink-600"
                autoFocus
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={createAPIKey}
                disabled={!newKeyName.trim() || creating}
                className="flex-1 bg-cyber text-void px-4 py-2.5 text-xs font-bold tracking-wider uppercase disabled:opacity-40 hover:shadow-[0_0_12px_rgba(0,229,255,0.4)] transition-shadow"
              >
                {creating ? "Creating…" : "Create Key"}
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewKeyName("");
                }}
                className="flex-1 border border-line-2 text-ink-300 px-4 py-2.5 text-xs font-bold tracking-wider uppercase hover:border-line-1 hover:text-ink-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-void/80 backdrop-blur-sm">
          <div className="bg-surface border border-hotpink/40 max-w-sm w-full mx-4 p-6">
            <div className="flex items-start gap-3 mb-5">
              <AlertTriangle size={20} className="text-hotpink mt-0.5 shrink-0" />
              <div>
                <div className="font-display font-bold text-lg">Delete API Key</div>
                <p className="text-xs text-ink-400 mt-1">
                  This will permanently delete{" "}
                  <span className="text-ink-100 font-semibold">"{deleteTarget.name}"</span>.
                  Any agents using it will lose access immediately.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 bg-hotpink text-void px-4 py-2.5 text-xs font-bold tracking-wider uppercase disabled:opacity-40 hover:bg-hotpink/90 transition-colors"
              >
                {deleting ? "Deleting…" : "Delete Key"}
              </button>
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 border border-line-2 text-ink-300 px-4 py-2.5 text-xs font-bold tracking-wider uppercase hover:border-line-1 hover:text-ink-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
